import React, { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDAlert from "components/MDAlert";

import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import CoverLayout from "components/CoverLayout";
import bgImage from "assets/images/bg-sign-in.png";

import { useSessionContext } from "context/SessionContext";
import { LanguageContext } from "context";
import { supabase } from "lib/supabaseClient"; 

function UpdatePassword(): JSX.Element {
  const [newPassword, setNewPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState(false);  
  const [isSessionValid, setIsSessionValid] = useState<boolean>(false);  
  const [alertMessage, setAlertMessage] = useState("");
  const [alertColor, setAlertColor] = useState<"info" | "error" | "success">("info");
  const [showAlert, setShowAlert] = useState(false);

  const navigate = useNavigate();
  const { logout } = useSessionContext();
  const { t, language, setLanguage } = useContext(LanguageContext) as any;

  // 画面が開いた時にセッションが存在するかチェックする
  useEffect(() => {
    const checkSession = async () => {
      const { data } = await supabase.auth.getSession();
      if (data.session) {
        setIsSessionValid(true);
      } else {
        setAlertColor("error");
        setAlertMessage(t("update-password.session_error") || "認証セッションが見つかりません。もう一度メールのリンクから開き直してください。");
        setShowAlert(true);
      }
    };
    checkSession();
  }, [t]);

  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleUpdatePassword = async () => {
    // セッションが無効な場合は処理しない
    if (!isSessionValid) return;

    if (newPassword.trim().length < 8) {
      setAlertColor("error");
      setAlertMessage(t("update-password.alert_invalid_password") || "パスワードは8文字以上で入力してください。");
      setShowAlert(false);
      setTimeout(() => setShowAlert(true), 0);
      return;
    }

    try {
      setIsSubmitting(true);
      
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      setAlertColor("success");
      setAlertMessage(t("update-password.alert_success") || "パスワードが正常に更新されました。ログイン画面に移動します。");
      setShowAlert(false);
      setTimeout(() => setShowAlert(true), 0);

      await logout(); // セッションを確実に破棄するためにlogout関数を呼び出す

      setTimeout(() => {
        navigate("/pages/auth/Sign-In", { replace: true }); 
      }, 2000);
      
    } catch (err: any) {
      console.error("Update password error:", err);
      setAlertColor("error");
      setAlertMessage(err.message || t("update-password.alert_error") || "パスワードの更新に失敗しました。");
      setShowAlert(false);
      setTimeout(() => setShowAlert(true), 0);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isJa = language === "ja";

  return (
    <CoverLayout image={bgImage}>
      <Card>
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          mx={2}
          mt={-3}
          p={3}
          mb={1}
          textAlign="center"
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            {t("update-password.title") || "パスワード再設定"}
          </MDTypography>
          <MDTypography display="block" variant="button" color="white" my={1}>
            {t("update-password.subtitle") || "新しいパスワードを入力してください"}
          </MDTypography>
        </MDBox>
        
        <MDBox pt={4} pb={3} px={3}>
          {showAlert && (
            <MDBox mb={2}>
              <MDAlert color={alertColor as any} dismissible onClose={() => setShowAlert(false)}>
                {alertMessage}
              </MDAlert>
            </MDBox>
          )}

          <MDBox display="flex" justifyContent="flex-end" mb={2} gap={1}>
            <MDButton
              variant={isJa ? "contained" : "outlined"}
              color="info"
              size="small"
              onClick={() => setLanguage?.("ja")}
              sx={{ px: 2, py: 0.5, fontSize: "0.75rem" }}
            >
              日本語
            </MDButton>
            <MDButton
              variant={!isJa ? "contained" : "outlined"}
              color="info"
              size="small"
              onClick={() => setLanguage?.("en")}
              sx={{ px: 2, py: 0.5, fontSize: "0.75rem" }}
            >
              English
            </MDButton>
          </MDBox>
          
          <MDBox component="form" role="form" onSubmit={(e: any) => e.preventDefault()}>
            <MDBox mb={2}>
              <MDInput
                type={showPassword ? "text" : "password"}
                label={t("update-password.new_password") || "新しいパスワード"}
                variant="standard"
                fullWidth
                placeholder={t("update-password.new_password_ph") || "************"}
                InputLabelProps={{ shrink: true }}
                value={newPassword}
                onChange={(e: any) => setNewPassword(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </MDBox>
            
            <MDBox mt={4} mb={1}>
              <MDButton
                variant="gradient"
                color="info"
                fullWidth
                onClick={handleUpdatePassword}
                // セッションが無効な間はボタンを押せなくする
                disabled={isSubmitting || !isSessionValid}
              >
                {isSubmitting ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  t("update-password.update_btn") || "パスワードを更新"
                )}
              </MDButton>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>
    </CoverLayout>
  );
}

export default UpdatePassword;